import { ChangeDetectionStrategy, Component, Inject, Input } from '@angular/core';

import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { Store } from '@ngrx/store';
import Decimal from 'decimal.js';
import DigitalActions from 'packages/sports/common/betslip/digital/actions';
import { EpcotConfigService, EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';
import { Observable } from 'rxjs';

import {
    ILinearBetslipMessagesProviderService,
    LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
} from '../../../../../../base/services/betslip-messages-provider.service';
import { AlertType } from '../../../../../alerts/models';
import { ITaxationInfoViewModel } from '../../../../../taxation/components/digital/taxation-info.component';
import { BetslipResultActions } from '../../../../actions';
import { LinearResultSummaryViewModel } from '../../../../models/linear-result-summary.model';
import { selectLinearResultViewModel } from '../../../../selectors';

@Component({
    selector: 'bs-linear-result-summary',
    templateUrl: './linear-result-summary.component.html',
    styleUrls: ['./linear-result-summary.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LinearResultSummaryComponent {
    @Input() inColumn: boolean;

    isTaxationInfoVisible: boolean;

    readonly isEpcot = this.epcotConfigService.isEnabled(EpcotModule.Betslip);

    readonly failedBetsAlertType = AlertType.Warning;
    readonly viewModel$: Observable<LinearResultSummaryViewModel>;

    readonly messages: {
        totalStake: string;
        totalReturns: string;
        totalReturnsNet: string;
        goToMyBets: string;
        viewBetslip: string;
        numberOfBetsPlaced: string;
        closeResult: string;
        declinedBets: string;
        closeResultDone: string;
    };

    constructor(
        @Inject(LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: ILinearBetslipMessagesProviderService,
        private epcotConfigService: EpcotConfigService,
        private store: Store,
    ) {
        const {
            betslip: { common },
            linear,
        } = messageProvider.getMessages();

        this.messages = {
            totalStake: common.TotalStake,
            closeResult: common.CloseBetslip,
            totalReturns: linear.TotalReturns,
            goToMyBets: linear.GoToMyBets,
            viewBetslip: linear.ViewBetslip,
            numberOfBetsPlaced: linear.NumberOfBetsPlaced,
            declinedBets: linear.BetsDeclinedMessage,
            totalReturnsNet: linear.TotalReturnsNet,
            closeResultDone: linear.SettingsDone,
        };

        this.viewModel$ = store.select(selectLinearResultViewModel);
    }

    closeResult(): void {
        this.store.dispatch(BetslipResultActions.close());
        this.store.dispatch(DigitalActions.closePopup());
    }

    goToBetslip(): void {
        this.store.dispatch(BetslipResultActions.close());
    }

    goToMyBets(): void {
        this.store.dispatch(BetslipResultActions.goToMyBets());
    }

    getDeclinedBetsMessage(declinedBetsCount: number): string {
        return interpolateDoubleBracesString(this.messages.declinedBets, { count: declinedBetsCount });
    }

    showGrossReturns(totalReturns: Decimal, totalReturnsNet: Decimal): boolean {
        return totalReturns.equals(totalReturnsNet);
    }

    displayTaxationInfo(): void {
        this.isTaxationInfoVisible = true;
    }

    getTotalReturnsValue({ totalReturnsNet, showEmptyPossibleWinnings }: LinearResultSummaryViewModel): Decimal | null {
        return showEmptyPossibleWinnings ? null : totalReturnsNet;
    }

    getTaxationVm(totalReturns: Decimal, totalReturnsNet: Decimal): ITaxationInfoViewModel {
        return {
            possibleWinningsGross: totalReturns,
            possibleWinningsNet: totalReturnsNet,
            taxationAmount: totalReturns.minus(totalReturnsNet),
            taxationRate: null,
        };
    }
}
